import React, { useState, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import LoginPage from './pages/LoginPage';
import DashboardLayout from './components/Layout/DashboardLayout';
import AdminLayout from './components/Layout/AdminLayout';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import StaffDirectory from './pages/StaffDirectory';
import Leave from './pages/Leave';
import LeaveCalendar from './pages/LeaveCalendar';
import Certificates from './pages/Certificates';
import WorkExperience from './pages/WorkExperience';
import Education from './pages/Education';
import LearningDevelopment from './pages/LearningDevelopment';
import Notifications from './pages/Notifications';
import NotificationPreferences from './pages/NotificationPreferences';
import PettyCash from './pages/PettyCash';
import Transport from './pages/Transport';
import ExternalTokens from './pages/admin/ExternalTokens';
import AdminDashboard from './pages/AdminDashboard';
import EmployeeManagement from './pages/EmployeeManagement';
import SystemSettings from './pages/SystemSettings';
import DepartmentManagement from './pages/DepartmentManagement';
import SiteManagement from './pages/SiteManagement';
import LeaveManagement from './pages/LeaveManagement';
import PayrollManagement from './pages/PayrollManagement';
import OvertimeMonitoring from './pages/OvertimeMonitoring/index';
import RestDayMonitoring from './pages/RestDayMonitoring/index';
import LearningDevelopmentAdmin from './pages/admin/LearningDevelopment/index';
import TalentInsights from './pages/admin/TalentInsights/index';
import GovernmentReimbursement from './pages/GovernmentReimbursement';
import DocumentRequests from './pages/admin/DocumentRequests';
import EmployeeLifecycle from './pages/admin/EmployeeLifecycle';
import RecruitmentBoard from './pages/admin/RecruitmentBoard';
import CVGeneration from './pages/admin/CVGeneration';
import SystemMonitoring from './pages/admin/SystemMonitoring';
import UsageAnalytics from './pages/admin/UsageAnalytics';
import NotificationLog from './pages/admin/NotificationLog';
import { MonitoringProvider } from './contexts/MonitoringContext';
import { AssetDetailProvider } from './contexts/AssetDetailContext';
import NewOffboarding from './pages/admin/NewOffboarding';
import OffboardingStep1 from './pages/admin/OffboardingStep1';
import OffboardingStep2 from './pages/admin/OffboardingStep2';
import OffboardingStep3 from './pages/admin/OffboardingStep3';
import OffboardingStep4 from './pages/admin/OffboardingStep4';
import OffboardingStep5 from './pages/admin/OffboardingStep5';
import OffboardingStep6 from './pages/admin/OffboardingStep6';
import OffboardingStep7 from './pages/admin/OffboardingStep7';
import NewPreOnboarding from './pages/admin/NewPreOnboarding';
import Reports from './pages/admin/Reports';
import ProjectLabourCostPage from './pages/admin/reports/ProjectLabourCostPage';
import ProjectManpowerDeploymentPage from './pages/admin/reports/ProjectManpowerDeploymentPage';
import LabourMarketSurveyPage from './pages/admin/reports/LabourMarketSurveyPage';
// Explicit `/index` path to avoid module-resolution ambiguity in case a
// stale legacy `DormitoryManagement.js` is ever present in a deployed
// build tree (e.g. left behind by a non-mirroring robocopy sync). The
// legacy file no longer exists in dev — this guards prod regardless.
import DormitoryManagement from './pages/admin/DormitoryManagement/index';
import AssetInventory from './pages/admin/AssetInventory';
import BIKManagement from './pages/admin/BIKManagement';
import ApprovalWorkflows from './pages/admin/ApprovalWorkflows';
import HandbookManagement from './pages/admin/HandbookManagement';
import HandbookAcknowledgements from './pages/admin/HandbookAcknowledgements';
import PolicyAcknowledgements from './pages/admin/PolicyAcknowledgements';
import SelfService from './pages/SelfService';
import SelfServiceLetters from './pages/SelfServiceLetters';
import SelfServiceHandbook from './pages/SelfServiceHandbook';
import SelfServicePolicies from './pages/SelfServicePolicies';
import SelfServicePolicy from './pages/SelfServicePolicy';
import SelfServiceTrainingMaterials from './pages/SelfServiceTrainingMaterials';
import SelfServiceTrainingMaterial from './pages/SelfServiceTrainingMaterial';
import SelfServiceEvents, { EventDetail as SelfServiceEventDetail } from './pages/SelfServiceEvents';
import SelfServiceBigFive from './pages/SelfServiceBigFive';
import SelfServiceBigFiveConsent from './pages/SelfServiceBigFiveConsent';
import SelfServiceBigFiveTake from './pages/SelfServiceBigFiveTake';
import SelfServiceBigFiveResult from './pages/SelfServiceBigFiveResult';
import SelfServiceBigFiveAccessLog from './pages/SelfServiceBigFiveAccessLog';
import SelfServiceWhistleblowing from './pages/SelfServiceWhistleblowing';
import EventPresent from './pages/EventPresent';
import AttendEvent from './pages/AttendEvent';
import NightworkManagement from './pages/NightworkManagement';
import RestDayWorkManagement from './pages/RestDayWorkManagement';
import TradeTransferManagement from './pages/TradeTransferManagement';
import SelfServicePassportRequests from './pages/SelfServicePassportRequests';
import SelfServiceTravelNotice from './pages/SelfServiceTravelNotice';
import SelfServiceCourseRequests from './pages/SelfServiceCourseRequests';
import SelfServiceOffboarding from './pages/SelfServiceOffboarding';
import SelfServiceWorkPassDecisions from './pages/SelfServiceWorkPassDecisions';
import SelfServiceTerminationRequests from './pages/SelfServiceTerminationRequests';
import SelfServiceResignationNotice from './pages/SelfServiceResignationNotice';
import OffboardingTasks from './pages/OffboardingTasks';
import ROTeamDashboard from './pages/ROTeamDashboard';
import MyAssets from './pages/MyAssets';
import UserGuide from './pages/UserGuide';
import AssetAdminLayout from './components/Layout/AssetAdminLayout';
import AssetAdminDashboard from './pages/asset-admin/AssetAdminDashboard';
import AssetAdminAssets from './pages/asset-admin/AssetAdminAssets';
import AssetAdminTransfers from './pages/asset-admin/AssetAdminTransfers';
import AssetAdminChecklists from './pages/asset-admin/AssetAdminChecklists';
import AssetAdminDisposal from './pages/asset-admin/AssetAdminDisposal';
import AssetAdminCategories from './pages/asset-admin/AssetAdminCategories';
import PendingReturns from './pages/asset-admin/PendingReturns';
import AssetAdminCustodians from './pages/asset-admin/AssetAdminCustodians';
import AssetAdminExternalPersonnel from './pages/asset-admin/AssetAdminExternalPersonnel';
import AssetAdminApiAccess from './pages/asset-admin/AssetAdminApiAccess';
import AssetAdminEndpointSecurity from './pages/asset-admin/AssetAdminEndpointSecurity';
import AssetAdminFleetMaintenance from './pages/asset-admin/AssetAdminFleetMaintenance';
import AssetAdminFleetInspections from './pages/asset-admin/AssetAdminFleetInspections';
import AssetAdminFleetOffences from './pages/asset-admin/AssetAdminFleetOffences';
import AssetAdminFleetAccidents from './pages/asset-admin/AssetAdminFleetAccidents';
import PrivacyPolicy from './pages/PrivacyPolicy';
import ForgotPassword from './pages/ForgotPassword';
import SetupAccount from './pages/SetupAccount';
import ResetPassword from './pages/ResetPassword';
import PublicCardPage from './pages/PublicCardPage';
import CandidatePortal from './pages/CandidatePortal';
import AssetReceipt from './pages/AssetReceipt';

import LoadingSpinner from './components/UI/LoadingSpinner';
import PWAInstallPrompt from './components/UI/PWAInstallPrompt';
import ConsentModal from './components/UI/ConsentModal';
import PasswordChangeModal from './components/UI/PasswordChangeModal';
import LandscapeWarning from './components/UI/LandscapeWarning';

// Heavy pages — code-split to keep the initial bundle lean
const Attendance = lazy(() => import('./pages/Attendance'));
const AttendanceManagement = lazy(() => import('./pages/AttendanceManagement'));
const Payslips = lazy(() => import('./pages/Payslips'));
const WorkerCostFiles = lazy(() => import('./pages/WorkerCostFiles'));

// Protected Route Component
const ProtectedRoute = ({
  children,
  requireAdmin = false,
  requireHRAdmin = false,
  requirePayrollAccess = false,
}) => {
  const { user, canAccessAdmin } = useAuth();
  const location = useLocation();

  if (!user) {
    // Preserve the original destination so login can bounce back (e.g. /attend/:token QR flow)
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  if (requireHRAdmin && !(user?.has_admin_access || user?.is_superuser)) {
    // Align with backend IsHRAdmin — worker-admins must not reach a 403 page.
    return <Navigate to="/dashboard" replace />;
  }

  if (requireAdmin && !canAccessAdmin()) {
    return <Navigate to="/dashboard" replace />;
  }

  if (
    requirePayrollAccess
    && !canAccessAdmin()
    && !user?.portal_groups?.includes('Finance')
  ) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Redirects an authenticated user to their intended destination (?next=) when landing on /login.
// Falls back to /dashboard to preserve existing behaviour.
const PostLoginRedirect = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const raw = params.get('next');
  // Only permit internal paths to avoid open-redirect abuse.
  const safeNext = raw && raw.startsWith('/') && !raw.startsWith('//') ? raw : '/dashboard';
  return <Navigate to={safeNext} replace />;
};

// Redirect Executive Directors away from attendance (they are exempt)
const AttendanceRoute = ({ children }) => {
  const { user } = useAuth();
  if (user?.employment_classification === 'executive_director') {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Protected Route for Asset Custodians
const ProtectedAssetRoute = ({ children }) => {
  const { user } = useAuth();
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Check if user is an asset custodian
  if (!user.is_asset_custodian) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  const { 
    user, 
    loading, 
    consentObtained, 
    consentLoading: authConsentLoading, 
    acceptConsent,
    requiresPasswordChange,
    changePassword,
    canAccessAdmin
  } = useAuth();
  const [consentLoading, setConsentLoading] = useState(false);
  const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);
  const [passwordChangeError, setPasswordChangeError] = useState(null);

  // Handle consent acceptance
  const handleAcceptConsent = async () => {
    setConsentLoading(true);
    await acceptConsent();
    setConsentLoading(false);
  };

  // Handle password change
  const handlePasswordChange = async (currentPassword, newPassword) => {
    setPasswordChangeLoading(true);
    setPasswordChangeError(null);
    const result = await changePassword(currentPassword, newPassword);
    setPasswordChangeLoading(false);
    if (!result.success) {
      setPasswordChangeError(result.error);
    }
  };

  // Show loading spinner while checking authentication OR consent/password status
  // Block all access until both consent and password change status are definitively known
  const isInitializing = loading || (user && (consentObtained === null || requiresPasswordChange === null));
  
  if (isInitializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Determine if consent modal should be shown
  // Show modal when user is logged in and consent status is explicitly false
  const showConsentModal = user && consentObtained === false;
  
  // Determine if password change modal should be shown
  // Only show AFTER consent is obtained (sequential flow)
  const showPasswordChangeModal = user && consentObtained === true && requiresPasswordChange === true;
  
  // Calculate step numbers for progress indicator
  const needsConsent = consentObtained === false;
  const needsPasswordChange = requiresPasswordChange === true;
  const totalSteps = (needsConsent ? 1 : 0) + (needsPasswordChange ? 1 : 0);
  const currentPasswordStep = needsConsent ? 2 : 1;

  return (
    <div className="App">
      {/* PDPA Consent Modal - shown when consent not yet obtained */}
      <ConsentModal 
        isOpen={showConsentModal}
        onAccept={handleAcceptConsent}
        loading={consentLoading}
      />
      
      {/* Password Change Modal - shown after consent, when using default password */}
      <PasswordChangeModal
        isOpen={showPasswordChangeModal}
        onChangePassword={handlePasswordChange}
        loading={passwordChangeLoading}
        error={passwordChangeError}
        step={currentPasswordStep}
        totalSteps={totalSteps || 1}
      />
      
      <Suspense fallback={
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <LoadingSpinner size="lg" />
        </div>
      }>
      <Routes>
        {/* Public digital name card — accessible to anyone, no auth redirect */}
        <Route path="/card/:uuid" element={<PublicCardPage />} />

        {/* Candidate magic-link portal — anonymous, token-gated. */}
        <Route path="/candidate/portal" element={<CandidatePortal />} />
        <Route path="/candidate/portal/" element={<CandidatePortal />} />

        {/* Asset receipt acknowledgement — anonymous, token-gated (outsourced holders). */}
        <Route path="/asset-receipt" element={<AssetReceipt />} />

        <Route 
          path="/login" 
          element={!user ? <LoginPage /> : <PostLoginRedirect />} 
        />
        <Route 
          path="/forgot-password" 
          element={!user ? <ForgotPassword /> : <Navigate to="/dashboard" replace />} 
        />
        <Route 
          path="/setup-account" 
          element={!user ? <SetupAccount /> : <Navigate to="/dashboard" replace />} 
        />
        <Route 
          path="/reset-password" 
          element={!user ? <ResetPassword /> : <Navigate to="/dashboard" replace />} 
        />
        
        {/* Privacy Policy - accessible to logged in users (for viewing from consent modal) */}
        <Route path="/privacy-policy" element={
          <ProtectedRoute>
            <PrivacyPolicy />
          </ProtectedRoute>
        } />
        
        {/* Protected User Routes */}
        <Route path="/" element={
          <ProtectedRoute>
            <Navigate to="/dashboard" replace />
          </ProtectedRoute>
        } />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <DashboardLayout><Dashboard /></DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute>
            <DashboardLayout><Profile /></DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/staff-directory" element={
          <ProtectedRoute>
            <DashboardLayout><StaffDirectory /></DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/leave/*" element={
          <ProtectedRoute>
            <DashboardLayout><Leave /></DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/leave-calendar" element={
          <ProtectedRoute>
            <DashboardLayout><LeaveCalendar /></DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/attendance" element={
          <ProtectedRoute>
            <AttendanceRoute>
              <DashboardLayout><Attendance /></DashboardLayout>
            </AttendanceRoute>
          </ProtectedRoute>
        } />
        <Route path="/payslips" element={
          <ProtectedRoute>
            <DashboardLayout><Payslips /></DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/worker-cost" element={
          <ProtectedRoute>
            <DashboardLayout><WorkerCostFiles /></DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/petty-cash" element={
          <ProtectedRoute>
            <DashboardLayout><PettyCash /></DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/transport" element={
          <ProtectedRoute>
            <DashboardLayout><Transport /></DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/certificates" element={
          <ProtectedRoute>
            <DashboardLayout><Certificates /></DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/work-experience" element={
          <ProtectedRoute>
            <DashboardLayout><WorkExperience /></DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/education" element={
          <ProtectedRoute>
            <DashboardLayout><Education /></DashboardLayout>
          </ProtectedRoute>
        } />
        {/* Replaced standalone Emergency Contacts page with redirect to Profile */}
        <Route path="/emergency-contacts" element={<Navigate to="/profile" replace />} />
        <Route path="/learning-development" element={
          <ProtectedRoute>
            <DashboardLayout><LearningDevelopment /></DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/notifications" element={
          <ProtectedRoute>
            <DashboardLayout><Notifications /></DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/notifications/preferences" element={
          <ProtectedRoute>
            <DashboardLayout><NotificationPreferences /></DashboardLayout>
          </ProtectedRoute>
        } />
        <Route path="/self-service" element={<ProtectedRoute><SelfService /></ProtectedRoute>} />
        <Route path="/self-service/letters" element={<ProtectedRoute><SelfServiceLetters /></ProtectedRoute>} />
        <Route path="/self-service/handbook" element={<ProtectedRoute><SelfServiceHandbook /></ProtectedRoute>} />
        <Route path="/self-service/policies" element={<ProtectedRoute><SelfServicePolicies /></ProtectedRoute>} />
        <Route path="/self-service/policies/:policyKey" element={<ProtectedRoute><SelfServicePolicy /></ProtectedRoute>} />
        <Route path="/self-service/training-materials" element={<ProtectedRoute><SelfServiceTrainingMaterials /></ProtectedRoute>} />
        <Route path="/self-service/training-materials/:materialKey" element={<ProtectedRoute><SelfServiceTrainingMaterial /></ProtectedRoute>} />
        <Route path="/self-service/nightwork" element={<ProtectedRoute><NightworkManagement /></ProtectedRoute>} />
        <Route path="/self-service/rest-day-work" element={<ProtectedRoute><RestDayWorkManagement /></ProtectedRoute>} />
        <Route path="/self-service/trade-transfer" element={<ProtectedRoute><TradeTransferManagement /></ProtectedRoute>} />
        <Route path="/self-service/passport-return" element={<ProtectedRoute><SelfServicePassportRequests /></ProtectedRoute>} />
        <Route path="/self-service/passport-requests" element={<Navigate to="/self-service/passport-return" replace />} />
        <Route path="/self-service/travel-notice" element={<ProtectedRoute><SelfServiceTravelNotice /></ProtectedRoute>} />
        <Route path="/self-service/course-requests" element={<ProtectedRoute><SelfServiceCourseRequests /></ProtectedRoute>} />
        <Route path="/self-service/offboarding" element={<ProtectedRoute><SelfServiceOffboarding /></ProtectedRoute>} />
        <Route path="/self-service/work-pass-decisions" element={<ProtectedRoute><SelfServiceWorkPassDecisions /></ProtectedRoute>} />
        <Route path="/self-service/termination-requests" element={<ProtectedRoute><SelfServiceTerminationRequests /></ProtectedRoute>} />
        <Route path="/self-service/resignation-notice" element={<ProtectedRoute><SelfServiceResignationNotice /></ProtectedRoute>} />
        <Route path="/offboarding-tasks" element={<ProtectedRoute><OffboardingTasks /></ProtectedRoute>} />
        <Route path="/self-service/events" element={<ProtectedRoute><SelfServiceEvents /></ProtectedRoute>} />
        <Route path="/self-service/whistleblowing" element={<ProtectedRoute><SelfServiceWhistleblowing /></ProtectedRoute>} />
        <Route path="/self-service/events/:eventId" element={<ProtectedRoute><SelfServiceEventDetail /></ProtectedRoute>} />
        <Route path="/self-service/events/:eventId/present" element={<ProtectedRoute><EventPresent /></ProtectedRoute>} />

        {/* Big Five Profile (Talent Assessments) — voluntary, confidential. */}
        <Route path="/self-service/big-five" element={<ProtectedRoute><SelfServiceBigFive /></ProtectedRoute>} />
        <Route path="/self-service/big-five/consent" element={<ProtectedRoute><SelfServiceBigFiveConsent /></ProtectedRoute>} />
        <Route path="/self-service/big-five/take/:sessionId" element={<ProtectedRoute><SelfServiceBigFiveTake /></ProtectedRoute>} />
        <Route path="/self-service/big-five/result" element={<ProtectedRoute><SelfServiceBigFiveResult /></ProtectedRoute>} />
        <Route path="/self-service/big-five/access-log" element={<ProtectedRoute><SelfServiceBigFiveAccessLog /></ProtectedRoute>} />

        {/* Public attend page — uses QR token. Internal users will be forced to log in; external users can check in directly. */}
        <Route path="/attend/:token" element={<AttendEvent />} />

        {/* RO Team Dashboard */}
        <Route path="/team-dashboard" element={
          <ProtectedRoute>
            <DashboardLayout><ROTeamDashboard /></DashboardLayout>
          </ProtectedRoute>
        } />
        
        {/* My Assets (Employee Self-Service) */}
        <Route path="/my-assets" element={<ProtectedRoute><MyAssets /></ProtectedRoute>} />

        {/* User Guide — dedicated page. Entry points: sidebar nav ("User Guide")
            and the account dropdown menu (see DashboardLayout.js). */}
        <Route path="/user-guide" element={<ProtectedRoute><UserGuide /></ProtectedRoute>} />


        {/* Protected Admin Routes */}
        <Route path="/admin" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout><AdminDashboard /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/dashboard" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout><AdminDashboard /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/usage" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout><UsageAnalytics /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/employees" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout><EmployeeManagement /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/documents" element={
          <ProtectedRoute requireAdmin={true}>
            <Navigate to="/dashboard#pending-verification" replace />
          </ProtectedRoute>
        } />
        <Route path="/admin/employee-lifecycle" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout><EmployeeLifecycle /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/recruitment" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout><RecruitmentBoard /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/employee-lifecycle/offboarding/:workflowId" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout><EmployeeLifecycle /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/employee-lifecycle/new-offboarding" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout><NewOffboarding /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/employee-lifecycle/offboarding/:workflowId/step-1" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout><OffboardingStep1 /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/employee-lifecycle/offboarding/:workflowId/step-2" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout><OffboardingStep2 /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/employee-lifecycle/offboarding/:workflowId/step-3" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout><OffboardingStep3 /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/employee-lifecycle/offboarding/:workflowId/step-4" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout><OffboardingStep4 /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/employee-lifecycle/offboarding/:workflowId/step-5" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout><OffboardingStep5 /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/employee-lifecycle/offboarding/:workflowId/step-6" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout><OffboardingStep6 /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/employee-lifecycle/offboarding/:workflowId/step-7" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout><OffboardingStep7 /></AdminLayout>
          </ProtectedRoute>
        } />
        {/* Onboarding pipeline (CandidateApplication-backed).
            All deep-links land on the same EmployeeLifecycle page which now
            renders the 2-pane OnboardingBoard for the onboarding tab.
            The path-style :publicRef is forwarded to the board via useParams.
            NewPreOnboarding.js is kept as a fallback full-page intake form
            for direct URL access; the board itself uses an inline modal. */}
        <Route path="/admin/employee-lifecycle/new-onboarding" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout><NewPreOnboarding /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/employee-lifecycle/onboarding/:publicRef" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout><EmployeeLifecycle /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/employee-lifecycle/new-pre-onboarding" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout><NewPreOnboarding /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/employee-lifecycle/pre-onboarding/:publicRef" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout><EmployeeLifecycle /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/site" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout>
              <SiteManagement />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/departments" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout>
              <DepartmentManagement />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/attendance" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout>
              <AttendanceManagement />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/leave" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout>
              <LeaveManagement />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/payroll" element={
          <ProtectedRoute requirePayrollAccess={true}>
            <AdminLayout>
              <PayrollManagement />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/attendance/overtime-monitoring" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout>
              <OvertimeMonitoring />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/attendance/rest-day-monitoring" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout>
              <RestDayMonitoring />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/learning-development" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout><LearningDevelopmentAdmin /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/talent-insights" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout><TalentInsights /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/external-tokens" element={
          <ProtectedRoute requireHRAdmin={true}>
            <AdminLayout><ExternalTokens /></AdminLayout>
          </ProtectedRoute>
        } />
        {/* Legacy path support */}
        <Route path="/admin/system-settings" element={<Navigate to="/admin/settings" replace />} />
        <Route path="/admin/settings" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout><SystemSettings /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/system-monitoring" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout>
              <MonitoringProvider>
                <SystemMonitoring />
              </MonitoringProvider>
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/notification-log" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout><NotificationLog /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/government-reimbursement" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout><GovernmentReimbursement /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/document-requests" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout><DocumentRequests /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/cv-generation" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout><CVGeneration /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/reports" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout><Reports /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/reports/project-labour-cost" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout><ProjectLabourCostPage /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/reports/project-manpower-deployment" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout><ProjectManpowerDeploymentPage /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/reports/labour-market-survey" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout><LabourMarketSurveyPage /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/dormitory-management" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout><DormitoryManagement /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/asset-inventory" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout><AssetInventory /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/bik-management" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout><BIKManagement /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/approval-workflows" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout><ApprovalWorkflows /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/handbook" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout><HandbookManagement /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/handbook/acknowledgements" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout><HandbookAcknowledgements /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/policies/acknowledgements" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminLayout><PolicyAcknowledgements /></AdminLayout>
          </ProtectedRoute>
        } />

        {/* Asset Admin Portal Routes (for Asset Custodians) */}
        <Route path="/asset-admin" element={
          <ProtectedAssetRoute>
            <AssetDetailProvider>
              <AssetAdminLayout>
                <Outlet />
              </AssetAdminLayout>
            </AssetDetailProvider>
          </ProtectedAssetRoute>
        }>
          <Route index element={<AssetAdminDashboard />} />
          <Route path="assets" element={<AssetAdminAssets />} />
          <Route path="transfers" element={<AssetAdminTransfers />} />
          <Route path="checklists" element={<AssetAdminChecklists />} />
          <Route path="disposal" element={<AssetAdminDisposal />} />
          <Route path="categories" element={<AssetAdminCategories />} />
          <Route path="pending-returns" element={<PendingReturns />} />
          <Route path="endpoint-security" element={<AssetAdminEndpointSecurity />} />
          <Route path="fleet-maintenance" element={<AssetAdminFleetMaintenance />} />
          <Route path="fleet-inspections" element={<AssetAdminFleetInspections />} />
          <Route path="fleet-offences" element={<AssetAdminFleetOffences />} />
          <Route path="fleet-accidents" element={<AssetAdminFleetAccidents />} />
          <Route path="custodians" element={<AssetAdminCustodians />} />
          <Route path="external-personnel" element={<AssetAdminExternalPersonnel />} />
          <Route path="api-access" element={<AssetAdminApiAccess />} />
        </Route>

        {/* Catch-all: redirect unmatched paths to dashboard */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      </Suspense>
      <PWAInstallPrompt />
      <LandscapeWarning />
    </div>
  );
}

export default App; 